import DOMPurify from 'dompurify';

/*
    HTML we produce, rather than HTML we receive.

    Reader.tsx sanitises hostile mail from strangers and renders it in a sandbox.
    This is the other direction: a small, closed vocabulary of tags that every
    mail client on earth renders the same way. Outlook still lays out with tables
    and Gmail strips anything it doesn't recognise, so a composer that emits
    modern CSS produces mail that looks broken on arrival — the constraint is the
    feature.

    Everything leaving the composer passes through `sanitiseOutgoing` even though
    we wrote it ourselves, because a paste from Word carries a kilobyte of
    mso-* styling per paragraph and a paste from a web page carries scripts.
*/

const TAGS = [
    'p', 'br', 'div', 'span',
    'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'sub', 'sup',
    'a', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
    'h1', 'h2', 'h3', 'h4',
    'img', 'hr',
    'table', 'thead', 'tbody', 'tr', 'td', 'th',
];

const ATTRS = ['href', 'src', 'alt', 'title', 'width', 'height', 'style', 'align', 'colspan', 'rowspan', 'dir', 'type'];

/*
    Inline style is allowed because it is the only styling mail clients honour,
    but only these properties survive. `position` and `display` are the ones a
    pasted page uses to hide text, and neither belongs in a message.
*/
const STYLE_PROPS = new Set([
    'color', 'background-color', 'font-weight', 'font-style', 'text-decoration', 'text-decoration-line',
    'font-size', 'font-family', 'text-align', 'margin', 'margin-left', 'margin-right', 'margin-top',
    'margin-bottom', 'padding', 'padding-left', 'border-left', 'border', 'border-collapse', 'width',
    'max-width', 'height', 'line-height', 'vertical-align', 'list-style-type',
]);

function cleanStyle(value: string): string {
    return value
        .split(';')
        .map((d) => d.trim())
        .filter(Boolean)
        .filter((d) => {
            const name = d.slice(0, d.indexOf(':')).trim().toLowerCase();
            if (!STYLE_PROPS.has(name)) return false;
            // url() in a style is either a tracker or an image we didn't put there.
            return !/url\s*\(/i.test(d) && !/expression\s*\(/i.test(d);
        })
        .join('; ');
}

/** Sanitises composer output. Keeps `cid:` and `data:` images; those are ours. */
export function sanitiseOutgoing(html: string): string {
    const clean = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: TAGS,
        ALLOWED_ATTR: ATTRS,
        ALLOW_DATA_ATTR: false,
        ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel|cid|data:image\/(?:png|jpeg|gif|webp|svg\+xml)):/i,
    });

    const doc = new DOMParser().parseFromString(clean, 'text/html');

    for (const el of Array.from(doc.querySelectorAll('[style]'))) {
        const kept = cleanStyle(el.getAttribute('style') ?? '');
        if (kept) el.setAttribute('style', kept);
        else el.removeAttribute('style');
    }

    // A bare <a> in mail wants a scheme; "www.x.com" resolves against the reader's
    // own origin otherwise, which is a broken link in every client.
    for (const a of Array.from(doc.querySelectorAll('a[href]'))) {
        const href = a.getAttribute('href') ?? '';
        if (/^(www\.|[\w-]+\.[a-z]{2,})/i.test(href) && !/^[a-z]+:/i.test(href)) {
            a.setAttribute('href', `https://${href}`);
        }
    }

    return doc.body.innerHTML;
}

/*
    The plain-text alternative.

    Always generated, never asked for: a recipient reading on a watch, a screen
    reader, or a client with HTML off gets this part, and "(no readable body)"
    is not an acceptable thing to send anyone. Layout is reproduced only as far
    as text can carry it — quotes, lists, links and paragraph breaks.
*/
export function htmlToText(html: string): string {
    const doc = new DOMParser().parseFromString(sanitiseOutgoing(html), 'text/html');

    const walk = (node: Node, depth: number, marker: string | null): string => {
        if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? '').replace(/\s+/g, ' ');
        if (node.nodeType !== Node.ELEMENT_NODE) return '';

        const el = node as Element;
        const tag = el.tagName.toLowerCase();
        const kids = (nextDepth = depth, nextMarker = marker) =>
            Array.from(el.childNodes).map((n) => walk(n, nextDepth, nextMarker)).join('');

        switch (tag) {
            case 'br':
                return '\n';
            case 'hr':
                return '\n----------\n';
            case 'img':
                return el.getAttribute('alt') ? `[${el.getAttribute('alt')}]` : '';
            case 'a': {
                const text = kids().trim();
                const href = el.getAttribute('href') ?? '';
                if (!href || href.startsWith('data:')) return text;
                // Don't repeat the URL when the link text already is the URL.
                return text && text !== href ? `${text} <${href}>` : href;
            }
            case 'blockquote': {
                const inner = kids(depth + 1).trim();
                return `\n${inner.split('\n').map((l) => `> ${l}`).join('\n')}\n`;
            }
            case 'ul':
                return `\n${Array.from(el.children).map((li) => walk(li, depth, '-')).join('')}`;
            case 'ol': {
                let n = 0;
                return `\n${Array.from(el.children).map((li) => walk(li, depth, `${++n}.`)).join('')}`;
            }
            case 'li':
                return `${'  '.repeat(depth)}${marker ?? '-'} ${kids(depth + 1, null).trim()}\n`;
            case 'p':
            case 'div':
            case 'h1':
            case 'h2':
            case 'h3':
            case 'h4':
            case 'tr':
                return `${kids()}\n`;
            case 'td':
            case 'th':
                return `${kids()}\t`;
            case 'pre':
                return `\n${el.textContent ?? ''}\n`;
            default:
                return kids();
        }
    };

    return walk(doc.body, 0, null)
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Plain text to HTML, preserving `>` quoting — used when switching a draft to rich. */
export function textToHtml(text: string): string {
    return text
        .split(/\n{2,}/)
        .map((block) => {
            const lines = block.split('\n');
            const quotedBlock = lines.every((l) => l.startsWith('>'));
            const body = lines
                .map((l) => escape(quotedBlock ? l.replace(/^>\s?/, '') : l))
                .join('<br>');
            return quotedBlock ? `<blockquote>${body}</blockquote>` : `<p>${body}</p>`;
        })
        .join('');
}

export interface InlineImage {
    cid: string;
    type: string;
    name: string;
    blob: Blob;
}

/*
    Pulls embedded images out of the body and replaces them with `cid:` references.

    An <img src="data:…"> is what a paste or a logo picker produces, and it is the
    wrong thing to send: Gmail strips data: images outright, and the base64 sits in
    the HTML part where it inflates the message and can't be de-duplicated. A
    hotlinked https: logo is worse — every image-blocking client (ours included)
    hides it, and fetching it tells the sender the message was opened.

    So the bytes ship as real inline parts and the HTML points at them, which is
    what every mail client has understood since 1998.
*/
export function extractInlineImages(html: string, seed = 0): { html: string; images: InlineImage[] } {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const images: InlineImage[] = [];
    let n = seed;

    for (const img of Array.from(doc.querySelectorAll('img'))) {
        const src = img.getAttribute('src') ?? '';
        const match = /^data:([\w/+.-]+);base64,(.*)$/i.exec(src);
        if (!match) continue;
        const type = match[1] ?? 'application/octet-stream';
        const bytes = Uint8Array.from(atob(match[2] ?? ''), (c) => c.charCodeAt(0));
        const cid = `iris-${++n}@local`;
        const ext = (type.split('/')[1] ?? 'png').replace('+xml', '');
        images.push({ cid, type, name: `image-${n}.${ext}`, blob: new Blob([bytes], { type }) });
        img.setAttribute('src', `cid:${cid}`);
    }

    return { html: doc.body.innerHTML, images };
}

/** Whether a body has anything a reader would see — an image alone counts. */
export function hasContent(html: string): boolean {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Boolean(doc.body.textContent?.trim()) || doc.body.querySelector('img, hr, table') !== null;
}
